import { Modal, ModalWithTransition } from '@studiometa/ui';

/**
 * LanguageModal Class.
 *
 * Modale de localisation : select pays custom + select langue natif (avec drapeaux).
 * Le changement de pays recharge dynamiquement la liste des langues disponibles.
 */
export default class LanguageModal extends ModalWithTransition {
  isOpen = false;

  isCountryOpen = false;

  isLanguageOpen = false;

  static config = {
    name: 'LanguageModal',
    refs: [
      ...Modal.config.refs,
      'languages',
      'loader',
      'form',
      'countryBtn',
      'countryLabel',
      'countryInput',
      'countryList',
      'countryOption[]',
      'languageBtn',
      'languageLabel',
      'languageFlag',
      'languageInput',
      'languageList',
      'languageOption[]',
    ],
    options: {
      styles: {
        type: Object,
        /**
         * @returns {ModalStylesOption}
         */
        default: () => ({
          modal: {
            closed: {
              opacity: '0',
              pointerEvents: 'none',
            },
            open: {
              opacity: '1',
              pointerEvents: 'auto',
            },
          },
          overlay: {
            closed: 'opacity-0',
          },
          container: {
            closed: 'translate-y-20 opacity-0',
          },
        }),
      },
    },
  };

  /**
   * Toggle a custom dropdown (`country` or `language`).
   * @param {'country'|'language'} name
   * @param {boolean} open
   */
  toggleDropdown(name, open) {
    this[`is${name === 'country' ? 'Country' : 'Language'}Open`] = open;
    this.$refs[`${name}List`].classList.toggle('hidden', !open);
    this.$refs[`${name}Btn`].setAttribute('aria-expanded', String(open));
  }

  /**
   * Open/close the country dropdown on button click.
   */
  onCountryBtnClick() {
    this.toggleDropdown('country', !this.isCountryOpen);
  }

  /**
   * Open/close the language dropdown on button click.
   */
  onLanguageBtnClick() {
    this.toggleDropdown('language', !this.isLanguageOpen);
  }

  /**
   * Close the open dropdown when clicking outside of it.
   * @param {object} props
   * @param {{ target: EventTarget }} props.event
   */
  onDocumentClick({ event }) {
    ['country', 'language'].forEach((name) => {
      const btn = this.$refs[`${name}Btn`];
      const list = this.$refs[`${name}List`];
      const isOpen = name === 'country' ? this.isCountryOpen : this.isLanguageOpen;

      if (isOpen && btn && list && !btn.contains(event.target) && !list.contains(event.target)) {
        this.toggleDropdown(name, false);
      }
    });
  }

  /**
   * Mark the chosen option as selected within a dropdown.
   * @param {HTMLElement[]} options
   * @param {HTMLElement} chosen
   */
  setSelectedOption(options, chosen) {
    options.forEach((el) => {
      const isSelected = el === chosen;
      el.classList.toggle('underline', isSelected);
      el.parentElement?.setAttribute('aria-selected', String(isSelected));
    });
  }

  /**
   * Select a country: update the hidden field and the label, then
   * reload the available languages for the chosen country.
   * @param {object} props
   * @param {number} props.index Index of the clicked option.
   */
  onCountryOptionClick({ index }) {
    const option = this.$refs.countryOption[index];
    const { value, name } = option.dataset;

    this.$refs.countryInput.value = value;
    this.$refs.countryLabel.textContent = name;

    this.setSelectedOption(this.$refs.countryOption, option);
    this.toggleDropdown('country', false);
    this.reloadLanguages();
  }

  /**
   * Select a language: update the hidden field, the label and the flag.
   * @param {object} props
   * @param {number} props.index Index of the clicked option.
   */
  onLanguageOptionClick({ index }) {
    const option = this.$refs.languageOption[index];
    const { value, name } = option.dataset;

    this.$refs.languageInput.value = value;
    this.$refs.languageLabel.textContent = name;
    this.$refs.languageFlag.innerHTML = this.renderFlag(value);

    this.setSelectedOption(this.$refs.languageOption, option);
    this.toggleDropdown('language', false);
  }

  /**
   * Build a round flag image markup for a given ISO country code.
   * @param {string} iso
   * @returns {string}
   */
  renderFlag(iso) {
    // Code langue régional (ex. `pt-PT`) : on garde la partie pays (`pt`).
    // Le code langue `en` n'a pas de drapeau dédié : on retombe sur le drapeau britannique.
    const region = iso.toLowerCase().split('-').pop();
    const code = region === 'en' ? 'gb' : region;
    return `<img src="https://flagcdn.com/w40/${code}.png" srcset="https://flagcdn.com/w80/${code}.png 2x" width="20" height="20" loading="lazy" alt="" class="rounded-full object-cover shrink-0 size-5">`;
  }

  /**
   * Reload the available languages for the currently selected country.
   */
  reloadLanguages() {
    if (!this.$refs.form) {
      return;
    }

    this.$refs.loader.classList.remove('opacity-0');
    const formData = new FormData(this.$refs.form);

    fetch(this.$refs.form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const languagesElement = tempDiv.querySelector('#languages');

        if (languagesElement) {
          this.$refs.languages.innerHTML = languagesElement.innerHTML;
        }

        // Le dropdown langue a été re-rendu (fermé par défaut).
        this.isLanguageOpen = false;
        this.$update();
      })
      .catch(() => {
        // Silencieux : en cas d'échec, le dropdown langue conserve son état précédent.
      });
  }
}
